// src/components/TreeEditorSegment.jsx
import {
  Card,
  Typography,
  Slider,
  Space,
  Divider,
} from "antd";
import { useEffect, useMemo, useState, useCallback } from "react";
import { pushUndoAction } from "../ThreeJs/Global/UndoManager";
import { notifyIrradianceNeedsRecompute } from "../ThreeJs/Shading/IrradianceEvents";
import { TREES } from "../ThreeJs/Global/variables";

const { Text } = Typography;

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const METERS_TO_FEET = 3.28084;

const TreeEditorSegment = ({ selectedTree, setTrees, irradianceBusy = false }) => {
  // ✅ No conditional hooks
  const trunk = selectedTree?.userData?.trunk || null;
  const foliage = selectedTree?.userData?.foliage || null;
  const arrow = selectedTree?.userData?.arrow || null;

  const trunkBase = selectedTree?.userData?.__trunkBaseHeight ?? 1.2;
  const arrowOffset = selectedTree?.userData?.__arrowOffset ?? 2.1;

  const foliageType = useMemo(() => {
    const t = foliage?.geometry?.type || "";
    if (t.includes("Sphere")) return "sphere";
    if (t.includes("Cone")) return "cone";
    return "unknown";
  }, [foliage]);

  const foliageBase = useMemo(() => {
    const g = foliage?.geometry;
    const p = g?.parameters || {};
    const baseSphereRadius = typeof p.radius === "number" ? p.radius : 0.9;
    const baseConeHeight = typeof p.height === "number" ? p.height : 1.8;
    return {
      sphereRadius: baseSphereRadius,
      coneHeight: baseConeHeight,
      coneHalfHeight: baseConeHeight / 2,
    };
  }, [foliage]);

  const locked = !!selectedTree?.userData?.locked;

  const dimensionsFt = useMemo(() => {
    const sx = Math.abs(selectedTree?.scale?.x || 1);
    const sy = Math.abs(selectedTree?.scale?.y || 1);
    const sz = Math.abs(selectedTree?.scale?.z || 1);
    const trunkRadius = Math.max(
      Math.abs(trunk?.geometry?.parameters?.radiusTop || 0),
      Math.abs(trunk?.geometry?.parameters?.radiusBottom || 0)
    );
    const trunkHeight = (trunkBase || 0) * Math.abs(trunk?.scale?.y || 1) * sy;
    const canopyRadius = Math.abs(foliage?.geometry?.parameters?.radius || 0);
    const canopyHeight = foliageType === "sphere"
      ? canopyRadius * 2 * Math.abs(foliage?.scale?.y || 1) * sy
      : Math.abs(foliage?.geometry?.parameters?.height || 0) * Math.abs(foliage?.scale?.y || 1) * sy;
    return {
      trunkGirth: trunkRadius * 2 * Math.max(sx, sz) * Math.abs(trunk?.scale?.x || 1) * METERS_TO_FEET,
      trunkHeight: trunkHeight * METERS_TO_FEET,
      vegetationSpread: canopyRadius * 2 * Math.max(sx, sz) * Math.abs(foliage?.scale?.x || 1) * METERS_TO_FEET,
      vegetationHeight: canopyHeight * METERS_TO_FEET,
      totalHeight: (trunkHeight + canopyHeight) * METERS_TO_FEET,
    };
  }, [selectedTree, trunk, foliage, trunkBase, foliageType]);
  const ft = (value) => `${Number.isFinite(value) ? value.toFixed(2) : "0.00"} ft`;

  const captureTreeState = useCallback(() => {
    if (!selectedTree) return null;
    const t = selectedTree.userData?.trunk;
    const f = selectedTree.userData?.foliage;
    const a = selectedTree.userData?.arrow;
    return {
      trunkScale: t?.scale?.clone() || null,
      trunkPos: t?.position?.clone() || null,
      foliageScale: f?.scale?.clone() || null,
      foliagePos: f?.position?.clone() || null,
      arrowPos: a?.position?.clone() || null,
    };
  }, [selectedTree]);

  const applyTreeState = useCallback(
    (state) => {
      if (!selectedTree || !state) return;
      const t = selectedTree.userData?.trunk;
      const f = selectedTree.userData?.foliage;
      const a = selectedTree.userData?.arrow;
      if (t && state.trunkScale) t.scale.copy(state.trunkScale);
      if (t && state.trunkPos) t.position.copy(state.trunkPos);
      if (f && state.foliageScale) f.scale.copy(state.foliageScale);
      if (f && state.foliagePos) f.position.copy(state.foliagePos);
      if (a && state.arrowPos) a.position.copy(state.arrowPos);
      setTrunkGirth((Number(t?.scale?.x || 1) + Number(t?.scale?.z || 1)) / 2);
      setTrunkH(Number(t?.scale?.y || 1));
      setTopXZ(Number(f?.scale?.x || 1));
      setTopY(Number(f?.scale?.y || 1));
      setTrees?.([...(TREES?.trees || [])]);
    },
    [selectedTree, setTrees]
  );

  const commitTreeUndo = useCallback(
    (label, before, after) => {
      if (!before || !after) return;
      const changed = JSON.stringify(before) !== JSON.stringify(after);
      if (!changed) return;
      pushUndoAction({
        label,
        undo: () => {
          applyTreeState(before);
          notifyIrradianceNeedsRecompute("tree-updated");
        },
        redo: () => {
          applyTreeState(after);
          notifyIrradianceNeedsRecompute("tree-updated");
        },
      });
      notifyIrradianceNeedsRecompute("tree-updated");
    },
    [applyTreeState]
  );

  // ✅ Controlled UI state (smooth realtime sliders)
  const [trunkGirth, setTrunkGirth] = useState(1); // drives x & z
  const [trunkH, setTrunkH] = useState(1);

  // Top controls:
  // - sphere: radius (xz), height (y)
  // - cone: base thickness (xz), height (y)
  const [topXZ, setTopXZ] = useState(1);
  const [topY, setTopY] = useState(1);

  // Sync slider values when selection changes
  useEffect(() => {
    if (!selectedTree || !trunk) {
      setTrunkGirth(1);
      setTrunkH(1);
      setTopXZ(1);
      setTopY(1);
      return;
    }

    // girth: use avg of x and z (in case user had them different before)
    const x = Number(trunk.scale.x || 1);
    const z = Number(trunk.scale.z || 1);
    setTrunkGirth((x + z) / 2);

    setTrunkH(Number(trunk.scale.y || 1));

    if (foliage) {
      setTopXZ(Number(foliage.scale.x || 1));
      setTopY(Number(foliage.scale.y || 1));
    } else {
      setTopXZ(1);
      setTopY(1);
    }
  }, [selectedTree, trunk, foliage]);

  // Keep trunk on ground + foliage bottom on trunk top + arrow above trunk top
  const alignTree = useCallback(
    (nextTrunkH, nextTopY) => {
      if (!trunk) return;

      const h = clamp(nextTrunkH, 0.2, 6);

      // trunk is centered; keep its base on ground
      trunk.position.y = (trunkBase * h) / 2;
      const trunkTopY = trunkBase * h;

      if (foliage) {
        let halfHeightScaled = 0;

        if (foliageType === "sphere") {
          halfHeightScaled =
            foliageBase.sphereRadius * clamp(nextTopY, 0.2, 8);
        } else if (foliageType === "cone") {
          halfHeightScaled =
            foliageBase.coneHalfHeight * clamp(nextTopY, 0.2, 8);
        } else {
          halfHeightScaled =
            foliageBase.coneHalfHeight * clamp(nextTopY, 0.2, 8);
        }

        // bottom sits on trunkTopY
        foliage.position.y = trunkTopY + halfHeightScaled;
        // The handle must remain above the canopy; with real-world trees the
        // former trunk-top offset left it inside the foliage after an edit.
        if (arrow) {
          arrow.position.y = trunkTopY + 2 * halfHeightScaled + arrowOffset;
        }
      } else if (arrow) {
        arrow.position.y = trunkTopY + arrowOffset;
      }
    },
    [trunk, foliage, arrow, trunkBase, arrowOffset, foliageType, foliageBase]
  );

  // --- Handlers ---
  const setTrunkGirthBoth = (val) => {
    if (irradianceBusy) return;
    if (!trunk || locked) return;
    const before = captureTreeState();
    const g = clamp(val, 0.2, 5);
    trunk.scale.x = g;
    trunk.scale.z = g;
    setTrunkGirth(g);
    const after = captureTreeState();
    commitTreeUndo("Update tree", before, after);
  };

  const setTrunkHeight = (val) => {
    if (irradianceBusy) return;
    if (!trunk || locked) return;
    const before = captureTreeState();
    const h = clamp(val, 0.2, 6);
    trunk.scale.y = h;
    setTrunkH(h);
    alignTree(h, topY);
    const after = captureTreeState();
    commitTreeUndo("Update tree", before, after);
  };

  const setTopRadiusOrBase = (val) => {
    if (irradianceBusy) return;
    if (!foliage || locked) return;
    const before = captureTreeState();
    const xz = clamp(val, 0.2, 8);
    foliage.scale.x = xz;
    foliage.scale.z = xz;
    setTopXZ(xz);
    const after = captureTreeState();
    commitTreeUndo("Update tree", before, after);
  };

  const setTopHeight = (val) => {
    if (irradianceBusy) return;
    if (!foliage || locked) return;
    const before = captureTreeState();
    const y = clamp(val, 0.2, 8);
    foliage.scale.y = y;
    setTopY(y);
    alignTree(trunkH, y);
    const after = captureTreeState();
    commitTreeUndo("Update tree", before, after);
  };

  // Safe render return after hooks
  if (!selectedTree || !trunk) return null;

  return (
    <Card size="small" className="tree-editor-card">
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Text strong>Selected Tree</Text>
        <Text strong>{ft(dimensionsFt.totalHeight)}</Text>
      </Space>

      <Divider style={{ margin: "10px 0" }} />

      {/* TRUNK */}
      <div style={{ marginBottom: 10 }}>
        <Space
          className="tree-editor-metric"
          style={{ width: "100%", justifyContent: "space-between" }}
        >
          <Text>Trunk Girth</Text>
          <Text>{ft(dimensionsFt.trunkGirth)}</Text>
        </Space>
        <Slider
          min={0.2}
          max={5}
          step={0.05}
          value={trunkGirth}
          onChange={setTrunkGirthBoth}
          disabled={locked || irradianceBusy}
        />
      </div>

      <div style={{ marginBottom: 0 }}>
        <Space
          className="tree-editor-metric"
          style={{ width: "100%", justifyContent: "space-between" }}
        >
          <Text>Trunk Height</Text>
          <Text>{ft(dimensionsFt.trunkHeight)}</Text>
        </Space>
        <Slider
          min={0.2}
          max={6}
          step={0.05}
          value={trunkH}
          onChange={setTrunkHeight}
          disabled={locked || irradianceBusy}
        />
      </div>

      {/* TOP */}
      {foliage && (
        <>
          <Divider style={{ margin: "10px 0" }} />

          {foliageType === "sphere" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <Space
                  className="tree-editor-metric"
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <Text>Vegetation Spread</Text>
                  <Text>{ft(dimensionsFt.vegetationSpread)}</Text>
                </Space>
                <Slider
                  min={0.2}
                  max={8}
                  step={0.05}
                  value={topXZ}
                  onChange={setTopRadiusOrBase}
                  disabled={locked || irradianceBusy}
                />
              </div>

              <div style={{ marginBottom: 0 }}>
                <Space
                  className="tree-editor-metric"
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <Text>Vegetation Height</Text>
                  <Text>{ft(dimensionsFt.vegetationHeight)}</Text>
                </Space>
                <Slider
                  min={0.2}
                  max={8}
                  step={0.05}
                  value={topY}
                  onChange={setTopHeight}
                  disabled={locked || irradianceBusy}
                />
              </div>
            </>
          )}

          {foliageType === "cone" && (
            <>
              <div style={{ marginBottom: 10 }}>
                <Space
                  className="tree-editor-metric"
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <Text>Vegetation Spread</Text>
                  <Text>{ft(dimensionsFt.vegetationSpread)}</Text>
                </Space>
                <Slider
                  min={0.2}
                  max={8}
                  step={0.05}
                  value={topXZ}
                  onChange={setTopRadiusOrBase}
                  disabled={locked || irradianceBusy}
                />
              </div>

              <div style={{ marginBottom: 0 }}>
                <Space
                  className="tree-editor-metric"
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  <Text>Vegetation Height</Text>
                  <Text>{ft(dimensionsFt.vegetationHeight)}</Text>
                </Space>
                <Slider
                  min={0.2}
                  max={8}
                  step={0.05}
                  value={topY}
                  onChange={setTopHeight}
                  disabled={locked || irradianceBusy}
                />
              </div>
            </>
          )}

          {foliageType === "unknown" && (
            <Text type="secondary">
              Top controls unavailable (unknown foliage type).
            </Text>
          )}
        </>
      )}
    </Card>
  );
};

export default TreeEditorSegment;
